import React, { useEffect, useRef } from "react";
import { Select, Spin } from "antd";
import FloatFormGroup from "components/AntDesign/FloatFormGroup";

export type SelectOption<T extends number | string> = {
  value: T;
  label: string;
};

type FloatGroupProps = {
  label: string;
  floatMode?: "focus" | "value";
  status?: "error" | "warning" | "success";
  className?: string;
  hasPrefix?: boolean;
};

type CommonProps<T extends number | string> = {
  options: SelectOption<T>[];

  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  prefix?: React.ReactNode;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  onClear?: () => void;

  onSearch?: (value: string) => void;
  filterOption?:
    | boolean
    | ((input: string, option?: SelectOption<T>) => boolean);
  hasNextPage?: boolean;
  loadNextPage?: () => void;
  loadingMore?: boolean;
  float?: FloatGroupProps;
};

function useInfiniteScrollLock(loading: boolean) {
  const lockRef = useRef(false);

  useEffect(() => {
    if (!loading) lockRef.current = false;
  }, [loading]);

  const tryLock = () => {
    if (lockRef.current) return false;
    lockRef.current = true;
    return true;
  };

  return { tryLock };
}

type SingleProps<T extends number | string> = CommonProps<T> & {
  multiple?: false;
  value?: T;
  onChange: (value?: T) => void;
};

type MultipleProps<T extends number | string> = CommonProps<T> & {
  multiple: true;
  value: T[];
  onChange: (value: T[]) => void;
};

export type BaseSelectProps<T extends number | string> =
  | SingleProps<T>
  | MultipleProps<T>;

export function BaseSelect<T extends number | string>(
  props: SingleProps<T>,
): React.ReactElement;

export function BaseSelect<T extends number | string>(
  props: MultipleProps<T>,
): React.ReactElement;

export function BaseSelect<T extends number | string>(
  props: BaseSelectProps<T>,
) {
  const {
    options,
    disabled = false,
    loading = false,
    placeholder,
    prefix,
    onFocus,
    onBlur,
    onClear,
    onSearch,
    filterOption,
    hasNextPage = false,
    loadNextPage,
    loadingMore = false,
  } = props;

  const multiple = props.multiple === true;
  const { tryLock } = useInfiniteScrollLock(loadingMore);
  const lastScrollTopRef = useRef(0);

  useEffect(() => {
    lastScrollTopRef.current = 0;
  }, [options.length]);

  const handlePopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!loadNextPage || !hasNextPage || loadingMore) return;

    const target = event.currentTarget;
    if (target.scrollHeight <= target.clientHeight) return;

    const currentScrollTop = target.scrollTop;
    if (currentScrollTop <= lastScrollTopRef.current) return;
    lastScrollTopRef.current = currentScrollTop;

    const reachedBottom =
      currentScrollTop + target.clientHeight >= target.scrollHeight - 16;

    if (reachedBottom && tryLock()) loadNextPage();
  };

  const resolvedFilterOption =
    typeof filterOption !== "undefined"
      ? filterOption
      : (input: string, option?: SelectOption<T>) =>
          String(option?.label ?? "")
            .toLowerCase()
            .includes(input.toLowerCase());

  const select = (
    <Select
      className="select_antd"
      showSearch
      allowClear
      filterOption={resolvedFilterOption as any}
      virtual={false}
      mode={multiple ? "multiple" : undefined}
      value={multiple ? (props.value ?? []) : props.value}
      options={options}
      disabled={disabled}
      loading={loading}
      placeholder={placeholder}
      prefix={prefix}
      onSearch={onSearch}
      onFocus={onFocus}
      onBlur={onBlur}
      onClear={onClear}
      onChange={(v) => props.onChange(v as any)}
      onPopupScroll={handlePopupScroll}
      dropdownRender={(menu) => (
        <div style={{ position: "relative" }}>
          {menu}
          {loadingMore && (
            <div
              style={{
                position: "absolute",
                bottom: 4,
                left: 0,
                right: 0,
                display: "flex",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <Spin size="small" />
            </div>
          )}
        </div>
      )}
      getPopupContainer={(c) => c.parentElement ?? document.body}
      style={{ width: "100%" }}
    />
  );

  if (!props.float) {
    return select;
  }

  return (
    <FloatFormGroup
      label={props.float.label}
      mode={props.float.floatMode}
      status={props.float.status}
      className={props.float.className}
      hasPrefix={props.float.hasPrefix}
    >
      {select}
    </FloatFormGroup>
  );
}
